import React from 'react';
import { useParams } from 'react-router-dom';
import {
  useSelfOnboardingCreateAccountMutation,
  useUserUpdateIdentityMutation,
  useUpdateWorkerMutation,
} from 'apollo/generated/client-operations';
import { useFlags } from 'featureFlags';
import {
  Form,
  FormOnSubmit,
  FormInputTypes,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { AlertService } from 'components/alertService';
import { AuthContext } from 'auth';
import { getGraphQLError } from 'utils/error';
import { getPhoneNumberAsE164 } from 'utils';
import { commonClasses } from 'containers/selfOnboarding/steps/common.style';
import {
  SelfOnboardingStepKey,
  SelfOnboardingStepProps,
  SelfOnboardingWorkerNameFormData,
} from 'containers/selfOnboarding/steps/types';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import {
  StepActions,
  StepInfo,
  StepInfoTitle,
} from 'containers/selfOnboarding/steps/components';
import { useSelfOnboardingFormCommonProps } from 'containers/selfOnboarding/helpers/forms';
import { useGetIsSelfOnboardingClosed } from 'containers/selfOnboarding/helpers/useGetIsSelfOnboardingClosed';
import {
  getDefaultValues as getNameDefaultValues,
  getUpdateInputs,
} from 'containers/selfOnboarding/steps/workerNameStep/WorkerNameStep.forms';
import { AccountCreationConsent } from './AccountCreationConsent';

type AccountCreationFormData = SelfOnboardingWorkerNameFormData;

/**
 * Account-creation step of the authenticated flow (Mutation A). Collects the
 * worker's name and creates their authenticated Odin account, linking it to the
 * OTP-verified FusionAuth user. No contractor / jobsiteWorker is involved — the
 * worker is bound to the jobsite afterwards at contractor selection (Mutation
 * B). Email is a jobsite-configurable field collected later, on the
 * jobsite-specific data step.
 *
 * Reached only by a brand-new worker in the forward flow (`isVisible: !worker`).
 * An existing worker can also land here via the review screen's "Full name"
 * edit, in which case it updates the name in place instead of creating.
 */
export function AuthAccountCreationStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const {
    state,
    updateState,
    navigation,
    stepConfig,
    localize,
    language,
    refetchData,
    currentPolicyDocuments,
  } = props;
  const { basicInfo, worker, userAccountId, phoneVerificationToken } = state;
  const { workerId } = worker ?? {};
  const { jobsiteInvitationId } = useParams<{ jobsiteInvitationId: string }>();
  const { goToNextStep, goToStep, state: navigationState } = navigation;
  const { isReview, autoFocusField } = navigationState;

  const { authState } = React.useContext(AuthContext);
  const { consentRecaptureGateEnabled } = useFlags();
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasAgreedToPolicies, setHasAgreedToPolicies] = React.useState(false);
  const [hasAgreedToSms, setHasAgreedToSms] = React.useState(false);

  // Capture platform policy consent inline only when creating a brand-new
  // account (not the review-edit path), the feature is enabled, and the launch
  // policies have been seeded. Inert (no checkbox, empty ids) otherwise; a
  // returning worker who slips through still hits the global re-capture gate.
  const policyDocuments = currentPolicyDocuments ?? [];
  const showPolicyConsent =
    !workerId && consentRecaptureGateEnabled && policyDocuments.length > 0;

  // SMS opt-in is mandatory for every brand-new self-onboarding account: the
  // create mutation (Mutation A) stamps the consent timestamp unconditionally,
  // so the box must gate account creation. Shown only on the create path (not
  // the review-edit path, which just renames an existing worker).
  const showSmsConsent = !workerId;
  const [selfOnboardingCreateAccount] =
    useSelfOnboardingCreateAccountMutation();
  const [updateUserIdentity] = useUserUpdateIdentityMutation();
  const [updateWorker] = useUpdateWorkerMutation();
  const { isSelfOnboardingClosed } = useGetIsSelfOnboardingClosed();

  const onSubmit: FormOnSubmit<AccountCreationFormData> = async (
    data,
    event,
    dirtyFields,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      if (workerId) {
        // Existing worker (review edit): update the name in place.
        if (await isSelfOnboardingClosed()) {
          setIsSaving(false);
          goToStep(SelfOnboardingStepKey.Closed);
          return;
        }
        const { userIdentityInput, workerInput } = getUpdateInputs(
          data,
          dirtyFields,
        );
        if (userIdentityInput) {
          await updateUserIdentity({
            variables: { input: { userAccountId, userIdentityInput } },
          });
        }
        if (workerInput) {
          await updateWorker({ variables: { workerId, workerInput } });
        }
        updateState({ basicInfo: { ...basicInfo, ...data } });
        setIsSaving(false);
        goToNextStep();
        return;
      }

      // Brand-new worker: create the authenticated account (Mutation A).
      const { firstName, middleInitial, lastName, suffix } = data;
      const { data: result } = await selfOnboardingCreateAccount({
        variables: {
          input: {
            createUserInput: {
              firstName,
              lastName,
              phoneNumber: getPhoneNumberAsE164(basicInfo?.phoneNumber),
            },
            workerInput: {
              jobsiteInvitationId,
              middleInitial: middleInitial || undefined,
              suffix: suffix || undefined,
              primaryLanguage: language,
              selfOnboardingCurrentStep:
                SelfOnboardingStepKey.ContractorSelection,
            },
            phoneVerificationToken,
            // Fan the aggregate consent checkbox out to the affirmed documents;
            // empty when the consent step is inactive.
            policyDocumentIds: showPolicyConsent
              ? policyDocuments.map((doc) => doc.id)
              : [],
            // Send the actual SMS opt-in checkbox value so the server records
            // consent from genuine acknowledgement, not from UI gating.
            smsOptInAcknowledged: hasAgreedToSms,
          },
        },
      });

      const { auth } = result.selfOnboardingCreateAccount ?? {};
      // Sign in and stash the collected identity. Set userAccountId immediately
      // so the (post-account) contractor-selection step is whitelisted on the
      // very next render — the full session state is rebuilt by the container
      // only after the refetch resolves, which would otherwise let the
      // navigation guard bounce the still-`worker: null` state back to the start.
      authState.signIn(auth.session.sessionToken, null);
      updateState({
        basicInfo: { ...basicInfo, ...data },
        userAccountId: auth.user.userAccountId,
      });
      await refetchData();
      setIsSaving(false);
      goToStep(SelfOnboardingStepKey.ContractorSelection);
    } catch (error) {
      event.preventDefault();
      setIsSaving(false);
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  };

  const inputs = React.useMemo<TypedFormInputs<AccountCreationFormData>>(
    () => ({
      firstName: {
        element: FormInputTypes.Field,
        label: 'First name',
        validation: { required: true },
        layout: [GridColSpan.Span6, GridColSpan.SmSpan6],
      },
      middleInitial: {
        element: FormInputTypes.Field,
        label: 'Middle initial',
        elementProps: { fieldType: 'custom', cleaveOptions: { blocks: [1] } },
        layout: [GridColSpan.Span6, GridColSpan.SmSpan6],
      },
      lastName: {
        element: FormInputTypes.Field,
        label: 'Last name',
        validation: { required: true },
        layout: [GridColSpan.Span6, GridColSpan.SmSpan6],
      },
      suffix: {
        element: FormInputTypes.Field,
        label: 'Suffix',
        layout: [GridColSpan.Span6, GridColSpan.SmSpan6],
      },
    }),
    [],
  );
  const defaultValues = React.useMemo<AccountCreationFormData>(
    () => getNameDefaultValues(basicInfo),
    [basicInfo],
  );
  const { fieldsConfig, localization } = useSelfOnboardingFormCommonProps(
    stepConfig,
    localize,
  );

  return (
    <div className={commonClasses.stepContainer}>
      <StepInfo>
        <StepInfoTitle
          title={`${localize(Copy.self_onboarding_worker_match_header)} 👋`}
          subtitle={localize(Copy.self_onboarding_worker_match_instructions)}
          withBottomBorder
          hideOnMobile
        />
      </StepInfo>
      <Form
        inputs={inputs}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        autoFocus={(autoFocusField as keyof AccountCreationFormData) ?? true}
        renderBelow={
          <>
            <AccountCreationConsent
              showPolicyConsent={showPolicyConsent}
              showSmsConsent={showSmsConsent}
              policyDocuments={policyDocuments}
              hasAgreedToPolicies={hasAgreedToPolicies}
              onAgreedToPoliciesChange={setHasAgreedToPolicies}
              hasAgreedToSms={hasAgreedToSms}
              onAgreedToSmsChange={setHasAgreedToSms}
            />
            <StepActions
              localize={localize}
              isReview={isReview}
              actions={isReview ? 'all' : 'continue'}
              // In review mode the skip button is "Cancel"; `goToNextStep` with
              // `isReview` set returns to the review step (discarding edits),
              // matching every other review-reachable step. Without it Cancel is
              // a dead button.
              onSkip={goToNextStep}
              continueActionEnabled={
                (!showPolicyConsent || hasAgreedToPolicies) &&
                (!showSmsConsent || hasAgreedToSms)
              }
              continueActionWithSpinner={isSaving}
            />
          </>
        }
        validationTriggers={[language]}
        className={commonClasses.form}
        inputsContainerClassName={commonClasses.formInputsContainer}
        fieldsConfig={fieldsConfig}
        localization={localization}
      />
    </div>
  );
}
